import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U3500ContextProvider } from './U3500Context';
import { defaultU3500InitLU, doInitLU, luHandleAppiError, type U3500InitLU } from './U3500Service';

export const NS = 'u3500';

export const U3500ROLES = {
  READ: 'U3500:READ',
  ADD: 'U3500:ADD',
} as const;
export type U3500ROLES = (typeof U3500ROLES)[keyof typeof U3500ROLES];

const U3500: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState(false);
  const [initLUData, setInitLUData] = useState<U3500InitLU>(defaultU3500InitLU);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  useEffect(() => {
    i18next.loadNamespaces(['u3500', 'u3121'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3500InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3500InitLU) => {
      setInitLUData({
        ...defaultU3500InitLU,
        ...initLU,
      });
      setInitLUReady(true);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erreur axios
    onError: ({ error }: { error: any }) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- aligné U3151

  useEffect(() => {
    if (!i18nReady || !initLUReady) return;

    setLogicalUnitTitle(t('title', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3500ContextProvider initLU={initLUData}>
      <Outlet />
    </U3500ContextProvider>
  );
};

export default U3500;
